import { useEffect, useRef } from "react";

export interface UseAfterPaintOptions {
  /**
   * Set false to skip the callback. Lets a caller run it only on the code path that needs it without
   * calling the hook conditionally.
   */
  enabled?: boolean;
  /**
   * How many animation frames to wait before running the callback.
   *
   * One frame is enough for layout the browser itself has settled. Raise it to 2 when reading a value
   * a third-party widget computes in its own post-paint pass: a virtualizer measured directly reports
   * a viewport of 0 on the first frame and its real size on the second, so a one-frame wait reads
   * zeroes. Prefer the smallest count that reliably clears the measurement.
   *
   * @default 1
   */
  frames?: number;
}

/**
 * Runs `callback` once per change to `deps`, after the browser has painted.
 *
 * For reading layout that only exists **after** paint — most often a third-party widget's measured
 * size, which it computes from its own rendered output. Neither effect flavor works there:
 * `useLayoutEffect` runs before paint, so the measurement is still zero, and a bare `useEffect` runs
 * after commit but can still land before the paint that produces the measurement.
 *
 * Deliberately narrow. This is for measuring painted layout, **not** a general "run this later"
 * escape hatch — reach for it only when a value cannot be read any earlier, and never to respond to
 * state changes or to derive state (both belong in an event handler or in render).
 *
 * The pending frame is cancelled on cleanup, so a fast unmount or a rapid dependency change never
 * leaves a stale callback to fire. `callback` is held in a ref, so it need not be memoized.
 *
 * @param callback - reads the painted layout; runs inside an animation frame
 * @param deps - re-runs the measurement when these change, like any effect's dependency list
 *
 * @example
 * ```tsx
 * // A virtualizer sizes itself from its rendered rows, so its viewport is 0 until the second frame.
 * useAfterPaint(
 *   () => {
 *     const { scrollSize, viewportSize } = virtualizerRef.current ?? {};
 *     if (viewportSize && scrollSize <= viewportSize) onFitsViewport();
 *   },
 *   [items],
 *   { enabled: !isLoading, frames: 2 }
 * );
 * ```
 */
export const useAfterPaint = (
  callback: () => void,
  deps: readonly unknown[],
  options: UseAfterPaintOptions = {}
): void => {
  const { enabled = true, frames = 1 } = options;
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (!enabled) return;

    let handle = 0;
    let remaining = Math.max(1, frames);
    const step = () => {
      remaining -= 1;
      if (remaining > 0) {
        handle = requestAnimationFrame(step);
        return;
      }
      callbackRef.current();
    };
    handle = requestAnimationFrame(step);

    return () => cancelAnimationFrame(handle);
    // The caller's `deps` are the measurement's real inputs; `callback` is intentionally excluded
    // (held in a ref) so an inline arrow does not re-measure on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, frames, ...deps]);
};
